import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, Switch, Route, matchPath } from 'react-router-dom';

import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';

import EnhancedReturnsActivationStepDetective, {
  ENHANCED_RETURNS_MODAL_PATH,
  ENHANCED_RETURNS_MODAL_STORE_ACTIVATION_PATH,
  ENHANCED_RETURNS_MODAL_SETUP_CARRIER_PATH,
  ENHANCED_RETURNS_MODAL_SETUP_RETURN_LOCATION_PATH,
  ENHANCED_RETURNS_MODAL_BLOCKED_PATH,
  ENHANCED_RETURNS_MODAL_STORE_SETUP_COMPLETE_PATH,
} from './enhanced-returns-activation-step-detective';
import messages from './messages';
import style from './style.module.less';
import ModalPortal from '../../../../../components/modal-portal';
import { EnhancedReturnsStoreStatus } from '../../../../../enums/enhanced-returns-store-status';
import { BASE_STORE_SETUP_URL } from '../../../../../routes/constants';
import { PusherService } from '../../../../../services/pusher-service';
import * as PusherMessages from '../../../../../services/pusher-service/pusher-contract-messages';
import Global from '../../../../../store/global-selectors';
import { refreshStoreList } from '../../../../../store/seller/seller.actions';
import { Workflows } from '../../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';
import AuthenticateWithMarketplace from '../authenticate-with-marketplace';
import BlockedStore from '../blocked-store';
import ReturnCarrierSetup from '../return-carrier-setup';
import ReturnLocationSetup from '../return-location-setup';
import StoreList from '../store-list';
import StoreSetupComplete from '../store-setup-complete';

export const ActivationModal = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const storeGuid = matchPath(location.pathname, {
    path: ENHANCED_RETURNS_MODAL_STORE_ACTIVATION_PATH,
    exact: true,
  })?.params?.storeGuid;
  const [setupWizardRedirectUrl] = useState(() => {
    if (location?.state?.redirectUrl) {
      return location.state.redirectUrl;
    }
  });
  const activeStores = useSelector(Global.selectSellerActiveStores);
  const storesThatSupportEnhancedReturns = useMemo(() => activeStores.filter((store) => store.supportsEnhancedReturns), [activeStores]);

  const store = useMemo(
    () => storesThatSupportEnhancedReturns.find((s) => s.storeGuid === storeGuid),
    [storesThatSupportEnhancedReturns, storeGuid],
  );

  const storeActivationStatus = store?.enhancedReturnsActivationStatus;
  const trackActivateEnhancedReturns = useTrackWorkflow(Workflows.ACTIVATE_ENHANCED_RETURNS_FLOW);
  const isStoreSetupCompleteForEnhancedReturns = store?.isStoreSetupCompleteForEnhancedReturns ?? false;

  const onClose = useCallback(() => {
    if (!isStoreSetupCompleteForEnhancedReturns) {
      trackActivateEnhancedReturns(WorkflowSteps.CANCEL, {
        storeActivationStatus,
      });
    }
    const redirectUrl = setupWizardRedirectUrl || BASE_STORE_SETUP_URL;
    history.push(redirectUrl);
  }, [isStoreSetupCompleteForEnhancedReturns, setupWizardRedirectUrl, history, trackActivateEnhancedReturns, storeActivationStatus]);

  const routeBasedOnStoreStatus = useCallback(
    (status) => {
      const activationPath = ENHANCED_RETURNS_MODAL_STORE_ACTIVATION_PATH.replace(':storeGuid', storeGuid);
      const carrierSetupPath = ENHANCED_RETURNS_MODAL_SETUP_CARRIER_PATH.replace(':storeGuid', storeGuid);
      const blockedPath = ENHANCED_RETURNS_MODAL_BLOCKED_PATH.replace(':storeGuid', storeGuid);
      const returnLocationPath = ENHANCED_RETURNS_MODAL_SETUP_RETURN_LOCATION_PATH.replace(':storeGuid', storeGuid);

      // Route based on storeActivationStatus
      switch (status) {
        case EnhancedReturnsStoreStatus.AuthenticationInitiated:
        case EnhancedReturnsStoreStatus.Pending:
        case EnhancedReturnsStoreStatus.Processing:
          // Route to activation path and display current status
          history.push(activationPath, { storeActivationStatus: status });
          break;

        case EnhancedReturnsStoreStatus.Authenticated:
          // Authentication should be complete, route to carrier setup path
          history.push(carrierSetupPath);
          break;

        case EnhancedReturnsStoreStatus.Blocked:
          history.push(blockedPath);
          break;

        case EnhancedReturnsStoreStatus.Inactive:
          history.push(activationPath);
          break;

        case EnhancedReturnsStoreStatus.DefaultCarrierSelected:
        case EnhancedReturnsStoreStatus.DefaultReturnLocationSelected:
          history.push(returnLocationPath);
          break;
        default:
          break;
      }
    },
    [history, storeGuid],
  );

  // Listen for the webhook event that provides periodic updates on the store activation process
  useEffect(() => {
    const unbindFromPusherEvent = PusherService.bindToEvent(PusherMessages.COMPLETE_STORE_ONBOARDING_PUSHER_EVENT_NAME, async (data) => {
      const storeStatus = data?.activationStatus;

      // update stores list app-wide when store status changes
      dispatch(refreshStoreList());
      if (storeGuid) routeBasedOnStoreStatus(storeStatus);
    });

    // Clean up the event listener when the component unmounts
    return () => {
      unbindFromPusherEvent();
    };
  }, [routeBasedOnStoreStatus, storeGuid, dispatch]);

  useEffect(() => {
    dispatch(refreshStoreList());
  }, [dispatch]);

  // Route based on store status whenever it changes
  useEffect(() => {
    if (storeActivationStatus) routeBasedOnStoreStatus(storeActivationStatus);
  }, [routeBasedOnStoreStatus, storeActivationStatus]);

  return (
    <ModalPortal onClose={onClose}>
      <Modal className={style.modalContent}>
        <ModalHeader className={style.modalHeader}>
          <Text className={style.modalHeaderText} textType={TextType.H4} value={messages.headerText} />
        </ModalHeader>

        <ModalBody className={style.modalBody}>
          <EnhancedReturnsActivationStepDetective />
          <Switch>
            <Route exact path={ENHANCED_RETURNS_MODAL_PATH}>
              <StoreList stores={storesThatSupportEnhancedReturns} />
            </Route>
            <Route exact path={ENHANCED_RETURNS_MODAL_STORE_ACTIVATION_PATH}>
              <AuthenticateWithMarketplace />
            </Route>
            <Route exact path={ENHANCED_RETURNS_MODAL_SETUP_CARRIER_PATH}>
              <ReturnCarrierSetup />
            </Route>
            <Route exact path={ENHANCED_RETURNS_MODAL_SETUP_RETURN_LOCATION_PATH}>
              <ReturnLocationSetup />
            </Route>
            <Route exact path={ENHANCED_RETURNS_MODAL_STORE_SETUP_COMPLETE_PATH}>
              <StoreSetupComplete />
            </Route>
            <Route exact path={ENHANCED_RETURNS_MODAL_BLOCKED_PATH}>
              <BlockedStore />
            </Route>
            <Route path='*'>
              <Text textType={TextType.Paragraph} value={messages.notFound} />
            </Route>
          </Switch>
        </ModalBody>
      </Modal>
    </ModalPortal>
  );
};
